import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { ResponseWithVisibility } from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import { useStationEvents } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/useStationEvents";
import { getAffectedStops } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/responseSchema";
import { EventInfoMessage } from "features/CenterContent/VehicleDetails/shared/EventInfoMessage";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Semantics } from "shared/components/drops/DropsBadge";
import {
  DialogBody,
  DialogRoot,
  DropsDialogContent,
  DropsDialogHeader,
  DropsStaticAlert,
  Flex,
  Skeleton,
  DropsTooltip,
} from "shared/components/Spor";
import { useStretchBuilder } from "stores/useStretchBuilder";

type EventActionProps = {
  uuids: string[];
};

export const StationEventAction: FC<EventActionProps> = ({ uuids }) => {
  const { t } = useTranslation();
  const queries = useStationEvents(uuids);
  const isQueriesPending = queries.some((query) => query.isPending);
  const isAllQueriesError = queries.every((query) => query.isError);
  const erroredQueries = useMemo(
    () =>
      uuids.filter(
        (uuid) =>
          !queries.find((query) => query.isSuccess && query.data.uuid === uuid),
      ),
    [uuids, queries],
  );

  const getStationInfoSemantics = (stationInfoType: string): Semantics => {
    switch (stationInfoType) {
      case "STATION_THREAT_LEVEL":
        return "alarm";
      case "STATION_WANTED_PERSON":
      case "STATION_DROPS_EVACUATION":
        return "secondaryAlarm";
      case "STATION_GENERAL_WARNING":
      case "STATION_GENERAL_INFO":
      case "STATION_DIFFICULT_CUSTOMER":
        return "warning";
      default:
        return "info";
    }
  };

  const closeAndResetStretchBuilder = useStretchBuilder(
    (state) => state.closeAndResetStretchBuilder,
  );
  const [askConfirmationForUuid, setAskConfirmationForUuid] = useState<
    string | null
  >(null);

  const setStretchBuilderModalState = useStretchBuilder(
    (state) => state.setStretchBuilderModalState,
  );

  const { mutateAsync: closeStationEvent, status: closeStatus } = useMutation({
    mutationFn: (uuid: string) =>
      mutationFnPOST<ResponseWithVisibility, undefined>(
        `${getBackendUrl()}/station-information/${uuid}/close`,
      ),
  });

  useEffect(() => {
    if (closeStatus === "success") {
      setTimeout(closeAndResetStretchBuilder, 1000);
    }
  }, [closeStatus]);
  return (
    <DialogRoot
      open
      onOpenChange={closeAndResetStretchBuilder}
      size="lg"
      placement="center"
    >
      <DropsDialogContent alignItems="center">
        <DropsDialogHeader>{t("Aktive stasjonshendelser")}</DropsDialogHeader>
        <DialogBody display="grid" gap="12px" alignSelf="stretch">
          {isQueriesPending && <Skeleton height="36px" />}
          {isAllQueriesError && (
            <DropsStaticAlert variant="error">
              {t("Kunne ikke hente hendelser for stasjon.")}
            </DropsStaticAlert>
          )}
          {!isQueriesPending && !isAllQueriesError && (
            <Flex flexDir="column" gap="12px">
              {queries.map(({ data: event, isSuccess }) => {
                if (isSuccess) {
                  return (
                    <EventInfoMessage
                      key={event.uuid}
                      title={getAffectedStops(event)}
                      subTitle={
                        event.message ??
                        t("Kunne ikke finne distribusjonstekst")
                      }
                      semantics={getStationInfoSemantics(
                        event.stationInformationType,
                      )}
                      versions={[event]}
                      showActionButtons
                      askConfirmation={askConfirmationForUuid === event.uuid}
                      setAskConfirmation={(value) =>
                        setAskConfirmationForUuid(value ? event.uuid : null)
                      }
                      setModalOpen={() =>
                        setStretchBuilderModalState({
                          type: "station",
                          eventInfo: event,
                          stretchBuilderMode: false,
                        })
                      }
                      onClose={() => closeStationEvent(event.uuid)}
                      initialOpenState
                    />
                  );
                }
                return null;
              })}

              {!!erroredQueries.length && (
                <DropsTooltip label={erroredQueries.join(", ")}>
                  <DropsStaticAlert variant="error" my="6px">
                    {t(
                      "Kunne ikke hente data for ytterligere {{count}} hendelser",
                      { count: erroredQueries.length },
                    )}
                  </DropsStaticAlert>
                </DropsTooltip>
              )}
            </Flex>
          )}
        </DialogBody>
      </DropsDialogContent>
    </DialogRoot>
  );
};
